import React, { useMemo } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import portfollio1Img from "../../assets/images/portfolio-1.png";
import portfollio2Img from "../../assets/images/portfolio-2.png";
import portfollio3Img from "../../assets/images/portfolio-3.png";
import portfollio4Img from "../../assets/images/portfolio-4.png";
import commercialPortfolioImg from "../../assets/images/portfolio-commercial-new.png";
import residentialPortfolioImg from "../../assets/images/portfolio-residential-new.png";
import officesPortfolioImg from "../../assets/images/portfolio-offices-new.png";
import otherPortfolioImg from "../../assets/images/portfolio-other-new.png";
import { useNavigate } from "react-router-dom";
import PortfolioMetrics from "./PortfolioMetrics";
import { useGetPortfolioSummaryQuery } from "store/slices/apis/portfolioSummarySlice";
import EnterprisePageShell from "components/EnterprisePageShell";
import { enterpriseTokens } from "styles/theme";


export const portfolioData = [
  {
    img: portfollio1Img,
    name: "FOURWRD - (Multi - Modal Logistics Park)",
    type: "FOURWRD",
    description:
      "Large-scale integrated logistics zone enabling seamless movement of goods through road, rail, and warehousing connectivity.",
    totalProjects: 1,
    revenue: 361.68,
    id: "1",
    landArea: "3.66 M",
    bua: "0.5 M",
    da: "0 M",
  },
  {
    img: portfollio2Img,
    name: "FOURCI",
    type: "FOURCI",
    description:
      "Premium, strategically located commercial hubs built to support modern industrial, retail, and business requirements.",
    totalProjects: 0,
    revenue: 210,
    id: "2",
    landArea: "110",
    bua: "1.75 M",
    da: "1.75 M",
  },
  {
    img: portfollio3Img,
    name: "FOURUN",
    type: "FOURUN",
    description:
      "Flexible warehousing and workspace ecosystem designed for agility, rapid deployment, and multi-industry use.",
    totalProjects: 15,
    revenue: 0,
    id: "3",
    landArea: "1,74,300",
    bua: "0",
    da: "0",
  },
  {
    img: portfollio4Img,
    name: "Way - Side Amenities",
    type: "Way - Side Amenities",
    description:
      "Well-planned facilities along highways and transport corridors to enhance commuter comfort and logistics efficiency.",
    totalProjects: 0,
    revenue: 0,
    id: "4",
    landArea: "20",
    bua: "2.75 M",
    da: "2.75 M",
  },
  {
    img: portfollio4Img,
    name: "Asset Under Management",
    type: "AUM",
    description:
      "Well-planned facilities along highways and transport corridors to enhance commuter comfort and logistics efficiency.",
    totalProjects: 0,
    revenue: 0,
    id: "5",
    landArea: "20",
    bua: "2.75 M",
    da: "2.75 M",
  },
];


const portfolioImageByType = {
  commercial: commercialPortfolioImg,
  residential: residentialPortfolioImg,
  offices: officesPortfolioImg,
  office: officesPortfolioImg,
  other: otherPortfolioImg,
};

const getPortfolioImage = (portfolioType, fallbackIndex = 0) => {
  const key = String(portfolioType || "").trim().toLowerCase();
  return portfolioImageByType[key] || [commercialPortfolioImg, residentialPortfolioImg, officesPortfolioImg, otherPortfolioImg][fallbackIndex % 4];
};

const Portfolio = () => {
  const navigate = useNavigate();
  const handleClick = (item) => {
    localStorage.setItem("projectType", item.type)
    localStorage.setItem("portfolioName", item.name || item.type || "Portfolio")
    if (item.id === "1" || item.id === "2" || item.id === "3") {
      navigate("/project-list");
    }
  };

  const { data, isFetching, isError } = useGetPortfolioSummaryQuery();

  const sortedData = useMemo(() => {
    if (!data?.data?.length) return [];

    return [...data.data].sort(
      (a, b) => a.sequence - b.sequence
    );
  }, [data?.data]);

  const updatedPortfolioData = sortedData.length > 0 ? sortedData?.map((item, i) => {
    return {
      img: getPortfolioImage(item?.portFolioType || item?.portfolioName, i),
      name: item?.portfolioName || "",
      type: item?.portFolioType || "",
      description: item?.description || "",
      totalProjects: item?.totalProjects,
      totalBudget: item?.totalBudget ?? "",
      id: String(item?.sequence) ?? "",
      landArea: item?.landArea ?? "",
      bua: item?.builtUpArea ?? "",
      da: item?.deliveredArea ?? "",
    }
  }) : []

  if (isFetching) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="error">
          Failed to load portfolio.
        </Typography>
      </Box>
    );
  }

  if (updatedPortfolioData?.length === 0) {
    return <Box
      sx={{
        minHeight: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography color="error">
        No Portfolio Data.
      </Typography>
    </Box>
  }

  return (
    <EnterprisePageShell
      eyebrow="Portfolio Command Center"
      title="Select a portfolio workspace"
      description="Move between business lines, review capacity, and open the project portfolio that needs attention."
      meta={
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {[
            ["Portfolios", updatedPortfolioData.length],
            ["Projects", updatedPortfolioData.reduce((sum, item) => sum + Number(item.totalProjects || 0), 0)],
            ["Budget", `${updatedPortfolioData.reduce((sum, item) => sum + Number(item.totalBudget || 0), 0).toFixed(0)} Cr`],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                px: 1.5,
                py: 0.9,
                borderRadius: "3px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.14)",
                minWidth: 116,
              }}
            >
              <Typography sx={{ color: "#CBD5E1", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{label}</Typography>
              <Typography sx={{ color: "#FFFFFF", fontSize: 20, fontWeight: 700 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.08fr 0.92fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        {updatedPortfolioData?.map((item, index) => (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "210px 1fr" },
              minHeight: 248,
              border: `1px solid ${enterpriseTokens.lineSoft}`,
              borderRadius: "3px",
              overflow: "hidden",
              cursor: "pointer",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 12px 34px rgba(15, 23, 42, 0.07)",
              transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: "#93C5FD",
                boxShadow: "0 20px 46px rgba(15, 23, 42, 0.12)",
              },
            }}
            onClick={() => handleClick(item)}
            key={item?.id || index}
          >
            <Box sx={{ position: "relative", minHeight: { xs: 180, md: "100%" } }}>
              <img
                style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
                src={item.img}
                alt={item.name}
              />
              <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(11,18,32,0.10), rgba(11,18,32,0.78))" }} />
              <Box
                sx={{
                  position: "absolute",
                  left: 14,
                  bottom: 14,
                  px: 1.2,
                  py: 0.7,
                  borderRadius: "999px",
                  backgroundColor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  color: "#FFFFFF",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                }}
              >
                {item.type || "PORTFOLIO"}
              </Box>
            </Box>
            <Box sx={{ p: 2.25, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "22px", lineHeight: "28px", color: enterpriseTokens.navy }}>
                  {item.name}
                </Typography>
                <Typography sx={{ mt: 1, fontWeight: 500, fontSize: "12px", lineHeight: "22px", color: enterpriseTokens.muted }}>
                  {item.description}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Box sx={{ px: 1.25, py: 0.8, borderRadius: "3px", bgcolor: enterpriseTokens.surfaceTint, color: enterpriseTokens.primary, fontSize: 13, fontWeight: 600 }}>
                  {item.totalProjects} Projects
                </Box>
                {item?.totalBudget > 0 && (
                  <Box sx={{ px: 1.25, py: 0.8, borderRadius: "3px", bgcolor: "#ECFEFF", color: enterpriseTokens.accent, fontSize: 13, fontWeight: 600 }}>
                    ₹ {item.totalBudget} Cr
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: "auto" }}>
                <PortfolioMetrics
                  builtUpArea={item.bua}
                  landArea={item.landArea}
                  deliverArea={item.da}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </EnterprisePageShell>
  );
};

export default Portfolio;
